import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import api, { apiError } from "@/lib/api";

export default function Contact() {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <div className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 text-center">
          <p className="overline text-gold mb-3">{t("We're Here For You")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight">{t("Contact & Concierge")}</h1>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid lg:grid-cols-2 gap-16">
        <div>
          <h2 className="font-serif text-3xl font-light mb-8">{t("Get in touch")}</h2>
          <div className="space-y-6">
            {[
              { icon: MapPin, t: "Flagship Boutique", d: settings.location },
              { icon: Phone, t: "Call Us", d: settings.phone },
              { icon: Mail, t: "Email", d: settings.email },
              { icon: Clock, t: "Hours", d: settings.hours },
            ].map((c) => (
              <div key={c.t} className="flex items-start gap-4" data-testid={`contact-info-${c.t.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="h-11 w-11 border border-gold/40 grid place-items-center shrink-0"><c.icon className="h-5 w-5 text-gold" strokeWidth={1.3} /></div>
                <div><p className="text-sm font-medium">{t(c.t)}</p><p className="text-sm text-muted-foreground">{c.d}</p></div>
              </div>
            ))}
          </div>
          <div className="mt-10 border border-border p-6 bg-secondary/30">
            <h3 className="font-serif text-2xl mb-2">{t("Private Appointments")}</h3>
            <p className="text-sm text-muted-foreground">{t("Book a one-to-one consultation with our jewelry specialists, in-boutique or virtually.")}</p>
          </div>
        </div>

        <form onSubmit={submit} className="border border-border p-8" data-testid="contact-form">
          <h2 className="font-serif text-3xl font-light mb-6">{t("Send a message")}</h2>
          <div className="space-y-4">
            <div><Label className="overline text-muted-foreground" htmlFor="c-name">{t("Name")}</Label><Input id="c-name" required value={form.name} onChange={set("name")} className="rounded-none mt-1.5" data-testid="contact-name" /></div>
            <div><Label className="overline text-muted-foreground" htmlFor="c-email">{t("Email")}</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} className="rounded-none mt-1.5" data-testid="contact-email" /></div>
            <div><Label className="overline text-muted-foreground" htmlFor="c-subject">{t("Subject")}</Label><Input id="c-subject" value={form.subject} onChange={set("subject")} className="rounded-none mt-1.5" data-testid="contact-subject" /></div>
            <div><Label className="overline text-muted-foreground" htmlFor="c-message">{t("Message")}</Label><Textarea id="c-message" required rows={5} value={form.message} onChange={set("message")} className="rounded-none mt-1.5 resize-none" data-testid="contact-message" /></div>
            <Button type="submit" className="w-full rounded-none h-12" disabled={loading} data-testid="contact-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Send Message")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
